import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@/hooks/useTheme';
import { projectService } from '@/services/projectService';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { AddThresholdModal } from '@/components/Alerts/AddThresholdModal';
import { AlarmRulesTable, UiAlarmRule } from '@/components/Alerts/AlarmRulesTable';
import { AlarmTypeLite, ApiAlarmRule, AttributeTypeLite, getAlarmRules, getAlarmTypes, getAttributeTypes } from '@/api/alertsApi';

function iriId(iri: unknown): string | null {
  if (typeof iri !== 'string') return null;
  const last = iri.split('/').pop();
  return last ? last : null;
}

function normalizeRule(r: ApiAlarmRule, alarmTypes: AlarmTypeLite[] = []): UiAlarmRule {
  let typeLabel: string;
  
  if (typeof r.type === 'string') {
    // Type is an IRI, try to resolve it from alarmTypes
    const typeId = iriId(r.type);
    if (typeId) {
      const foundType = alarmTypes.find((t) => String(t.id) === typeId);
      typeLabel = foundType
        ? (foundType.label || foundType.code || `#${foundType.id}`)
        : `#${typeId}`;
    } else {
      typeLabel = r.type;
    }
  } else {
    // Type is an object
    typeLabel = (r.type as AlarmTypeLite).label || (r.type as AlarmTypeLite).code || `#${(r.type as AlarmTypeLite).id}`;
  }

  const target =
    r.box
      ? { kind: 'box' as const, value: r.box }
      : r.device
        ? { kind: 'device' as const, value: r.device }
        : r.attribute
          ? { kind: 'attribute' as const, value: r.attribute }
          : r.attributeType
            ? { kind: 'attribute_type' as const, value: r.attributeType }
            : null;

  return {
    id: r.id,
    enabled: r.enabled,
    typeLabel,
    scope: r.scope,
    target,
    operator: r.operator ?? null,
    thresholdValue: r.thresholdValue ?? null,
    offlineAfterSeconds: r.offlineAfterSeconds ?? null,
    cooldownSeconds: r.cooldownSeconds ?? null,
    updatedAt: r.updatedAt,
  };
}

export function AlertsConfigPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = projectId ? parseInt(projectId, 10) : null;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const [isAddThresholdModalOpen, setIsAddThresholdModalOpen] = useState(false);

  const { data: alarmTypes = [] } = useQuery({
    queryKey: ['alarmTypes'],
    queryFn: () => getAlarmTypes(),
  });

  const { data: attributeTypes = [] } = useQuery({
    queryKey: ['attributeTypes'],
    queryFn: () => getAttributeTypes(),
  });

  // Fetch threshold rules for attribute_type scope only
  const { data: rules = [], isLoading: isLoadingRules, error: rulesError } = useQuery({
    queryKey: ['alarmRules', projectIdNum, 'attribute_type', alarmTypes.length],
    queryFn: async () => {
      if (!projectIdNum) return [];
      const projectIri = `/api/projects/${projectIdNum}`;
      const apiRules = await getAlarmRules({ projectIri, scope: 'attribute_type' });
      return apiRules.map((r) => normalizeRule(r, alarmTypes));
    },
    enabled: !!projectIdNum && alarmTypes.length > 0,
  });

  const getUserInitials = (name: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const {
    data: project,
    isLoading: isLoadingProject,
    isError: projectError,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectService.getProject(Number(projectId)),
    enabled: !!projectId,
  });

  const handleAddThreshold = () => {
    setIsAddThresholdModalOpen(true);
  };

  if (!projectIdNum) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Projet non trouvé</p>
          <button onClick={() => navigate('/admin/dashboard')} className="mt-4 text-primary hover:text-primary-600">
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              <span className="text-slate-400 dark:text-slate-600">Admin</span> / Alertes
            </div>
            {project && (
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
                {project.city && (
                  <>
                    <span className="text-slate-400">•</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
                  </>
                )}
              </div>
            )}
            {projectError && (
              <div className="text-sm text-red-500">Projet introuvable</div>
            )}
            {isLoadingProject ? (
              <div className="text-sm text-slate-400">Chargement…</div>
            ) : null}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Seuils d'alerte</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Configuration des seuils par type d'attribut
              </p>
            </div>

            <button
              onClick={handleAddThreshold}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
            >
              <span className="material-icons-outlined" style={{ fontSize: '20px' }}>
                add
              </span>
              Ajouter Seuil
            </button>
          </div>

          <div className="bg-white dark:bg-[#16202A] rounded-lg border border-slate-200 dark:border-[#1E293B] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B]">
              <div className="flex items-center gap-2">
                <span className="material-icons-outlined text-slate-500 dark:text-slate-400" style={{ fontSize: '20px' }}>
                  tune
                </span>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Seuils configurés</h2>
              </div>
            </div>

            <div className="p-0">
              <AlarmRulesTable rules={rules} isLoading={isLoadingRules} error={rulesError} attributeTypes={attributeTypes} />
            </div>
          </div>
        </div>

        <AddThresholdModal
          isOpen={isAddThresholdModalOpen}
          onClose={() => setIsAddThresholdModalOpen(false)}
          projectId={projectIdNum}
          alarmTypes={alarmTypes}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['alarmRules'] });
            setIsAddThresholdModalOpen(false);
          }}
        />
      </main>
    </div>
  );
}

